import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material';
import { CloudUpload, VideoLibrary } from '@mui/icons-material';

interface VideoUploadFormProps {
  currentVideoUrl?: string;
  onVideoUploaded: (url: string) => void;
}

export default function VideoUploadForm({
  currentVideoUrl,
  onVideoUploaded,
}: VideoUploadFormProps) {
  const [videoUrl, setVideoUrl] = useState(currentVideoUrl || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setVideoUrl(url);
    onVideoUploaded(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload MP4, MOV, or WebM');
      return;
    }

    // Validate file size (max 2GB)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
    if (file.size > maxSize) {
      setError('File size exceeds 2GB limit');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // TODO: Implement actual Firebase Storage upload
      // For now, show a placeholder process
      
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200));
        setUploadProgress(i);
      }

      // Placeholder: In production, this would be the actual Firebase Storage URL
      const uploadedUrl = `https://storage.googleapis.com/sharegiver-training-videos/${file.name}`;
      setVideoUrl(uploadedUrl);
      onVideoUploaded(uploadedUrl);
      
      alert('Video uploaded successfully! (Note: This is a placeholder implementation)');
    } catch (err: any) {
      setError(err.message || 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Video */}
      {videoUrl && !uploading && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.50' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <VideoLibrary color="success" />
            <Box flex={1}>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                Video URL Set
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {videoUrl}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading video... {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Video URL Input */}
      <TextField
        fullWidth
        label="Video URL"
        value={videoUrl}
        onChange={handleUrlChange}
        placeholder="https://example.com/video.mp4 or paste YouTube/Vimeo URL"
        helperText="Enter a direct video URL or upload a file below"
        disabled={uploading}
        sx={{ mb: 3 }}
      />

      {/* File Upload */}
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Or upload a video file
        </Typography>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUpload />}
          disabled={uploading}
          fullWidth
        >
          {uploading ? 'Uploading...' : 'Upload Video File'}
          <input
            type="file"
            hidden
            accept="video/mp4,video/webm,video/quicktime"
            onChange={handleFileUpload}
          />
        </Button>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Supported formats: MP4, MOV, WebM (Max 2GB)
        </Typography>
      </Box>

      {!videoUrl && (
        <Alert severity="info" sx={{ mt: 3 }}>
          A video is required before publishing the course
        </Alert>
      )}
    </Box>
  );
}

